import { useEffect, useRef, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { Chart } from 'primereact/chart';
import { Timeline } from 'primereact/timeline';
import CustomDataTable from '../../components/CustomDataTable/CustomDataTable';
import CustomDatePicker from '../../components/CustomDatePicker/CustomDatePicker';
import moment from 'moment';
import { getAllDetails } from '../../service/ProductService';
import { DASHBOARD } from '../../config/ApiConstant';
import SkeletonDashboard from './SkeletonDashboard';
import { convertDateTime } from '../../appconfig/AppHelper';
import 'primeicons/primeicons.css';

const Dashboard = () => {
    document.title = 'Dashboard | GSPC';
    //Set Toast/ Filter Properties
    const toast = useRef<any>(null);
    // dropdown
    const [dashboardData, setDashboardData] = useState<any>();

    const [pageLoad, setPageLoad] = useState<any>(true);

    // Date Object
    let today = new Date();
    const [dates, setDates] = useState({
        start: new Date(today.setDate(today.getDate() - 31)),
        end: new Date()
    });

    const [showAllTransactions, setShowAllTransactions] = useState(false);

    const toggleTransactions = () => {
        setShowAllTransactions((prev) => !prev);
    };

    //chartData
    const [totalOrderData, setTotalOrderData] = useState({});
    const [totalOrderOptions, setTotalOrderOptions] = useState({});

    //Project status (Chart)
    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});

    // const lastYear = new Date(today.getFullYear() - 1, 0, 1);
    const [applicationYear, setApplicationYear] = useState<any>(new Date(today));

    useEffect(() => {
        const { start, end } = dates;
        getDashboardDetails(start, end, applicationYear);
    }, []);

    //api call for getDashboardDetails
    const getDashboardDetails = async (start: any, end: any, applicationYear: any) => {
        try {
            setPageLoad(true);
            let startDate = moment(start).format('y-M-D') + ' 00:00:00';
            let endDate = moment(end).format('y-M-D') + ' 23:59:59';
            let applicationYearFormat = moment(applicationYear).format('y');

            const response = await getAllDetails(DASHBOARD + '?start_date=' + startDate + '&end_date=' + endDate + '&year=' + applicationYearFormat);
            if (response) {
                setPageLoad(false);
                setDashboardData(response);
            }
        } catch (error: any) {
            if (error.response) {
                setPageLoad(false);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Sorry Could Not Load Data'
                });
            }
        }
    };

    //
    useEffect(() => {
        const documentStyle = getComputedStyle(document.documentElement);
        const data = {
            datasets: [
                {
                    data: [300, 50, 100],
                    backgroundColor: [documentStyle.getPropertyValue('--blue-500'), documentStyle.getPropertyValue('--yellow-500'), documentStyle.getPropertyValue('--green-500')],
                    hoverBackgroundColor: [documentStyle.getPropertyValue('--blue-400'), documentStyle.getPropertyValue('--yellow-400'), documentStyle.getPropertyValue('--green-400')]
                }
            ]
        };
        const options = {
            cutout: '90%'
        };

        setChartData(data);
        setChartOptions(options);
    }, []);

    // On Date Change
    const onDateChange = (start: any, end: any) => {
        setDates({ start, end });
        getDashboardDetails(start, end, applicationYear);
    };

    const handleDateChange = (value: any) => {
        const { start, end } = dates;
        setApplicationYear(value);
        getDashboardDetails(start, end, value);
    };

    const marker = (item: any) => {
        return (
            <span className="custom-marker" style={{ backgroundColor: '#28a745' }}>
                <i className="pi pi-check" style={{ color: 'white' }}></i>
            </span>
        );
    };

    const content = (item: any) => {
        return (
            <>
                <div className="flex align-items-center justify-content-between">
                    <p>Payment From #{item.BillSrNo}</p>
                    <h6>&#8377;{item.TotalAmt}</h6>
                </div>
                <p>{item.AppNm}</p>
                <span>{convertDateTime(item.created_at)}</span>
            </>
        );
    };

    // country distribution
    const countryChart = {
        labels: ['Degree Addition', 'Duplicate Certy ', 'Good Standing', 'Name Change', 'Re-Entry', 'Renew'],
        datasets: [
            {
                data: [
                    dashboardData?.pending_apps_list?.degree_addtion,
                    dashboardData?.pending_apps_list?.duplicate_certy,
                    dashboardData?.pending_apps_list?.good_standing,
                    dashboardData?.pending_apps_list?.name_change,
                    dashboardData?.pending_apps_list?.reentry,
                    dashboardData?.pending_apps_list?.renew
                ],
                backgroundColor: ['#0F8BFD', '#545C6B', '#EC4DBC', '#EEE500', '#FC6161', '#00D0DE', '#873EFE'],
                hoverBackgroundColor: ['#0F8BFD', '#545C6B', '#EC4DBC', '#EEE500', '#FC6161', '#00D0DE', '#873EFE'],
                borderColor: 'transparent',
                fill: true
            }
        ]
    };

    const countryChartOptions = {
        responsive: true
    };

    // Data table for Team members
    const orderYear = [
        { name: '2021', code: '0' },
        { name: '2020', code: '1' }
    ];

    //api call for Order Data
    useEffect(() => {
        getOrderData();
    }, [dashboardData?.applications]);

    //api call for Order Data
    const getOrderData = () => {
        let fresh: any = '';
        let freshOther: any = '';
        let reEntry: any = '';
        let transfer: any = '';

        let graphVal: any = '';
        let maxVal: any = '';

        if (!window.cn(dashboardData?.applications)) {
            fresh = dashboardData?.applications?.fresh.length > 0 && dashboardData?.applications?.fresh.map((item: any) => item?.total);

            freshOther = dashboardData?.applications.fresh_other.length > 0 && dashboardData?.applications.fresh_other.map((item: any) => item?.total);

            reEntry = dashboardData?.applications.reentry.length > 0 && dashboardData?.applications.reentry.map((item: any) => item?.total);

            transfer = dashboardData?.applications.transfer.length > 0 && dashboardData?.applications.transfer.map((item: any) => item?.total);

            if (!window.cn(graphVal) && graphVal.length > 0) {
                maxVal = Math.max(...graphVal);
            }
        }

        const data = {
            labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],

            datasets: [
                {
                    label: 'Fresh Registration',
                    data: fresh,
                    borderColor: '#00D0DE',
                    pointBackgroundColor: '#00D0DE',
                    backgroundColor: 'rgba(0, 208, 222, 0.05)',
                    fill: true,
                    tension: 0.4
                },

                {
                    label: 'Fresh Registration Other',
                    data: freshOther,
                    borderColor: '#EEE500',
                    pointBackgroundColor: '#EEE500',
                    backgroundColor: 'rgba(238, 229, 0, 0.05)',
                    fill: true,
                    tension: 0.4
                },
                {
                    label: 'Transfer To Gujarat',
                    data: transfer,
                    borderColor: '#FC6161',
                    pointBackgroundColor: '#FC6161',
                    backgroundColor: 'rgba(253, 72, 74, 0.05)',
                    fill: true,
                    tension: 0.4
                },
                {
                    label: 'Re-entry',
                    data: reEntry,
                    borderColor: '#0F8BFD',
                    pointBackgroundColor: '#0F8BFD',
                    backgroundColor: 'rgba(15, 139, 253, 0.05)',
                    fill: true,
                    tension: 0.4
                }
            ]
        };
        const options = {
            maintainAspectRatio: false,
            aspectRatio: 1,
            plugins: {
                legend: {
                    display: true
                }
            },
            responsive: true,
            hover: {
                mode: 'index'
            },
            scales: {
                y: {
                    // min: 0
                    // max: !window.cn(maxVal) && maxVal <= 20 ? 20 + 2 : maxVal + 5,
                    ticks: {
                        stepSize: 100
                    }
                    // ticks: 100
                }
            }
        };

        setTotalOrderData(data);
        setTotalOrderOptions(options);
    };


    return (
        <>
            <div className="top-bar">
                <div className="top-intro">{/* <p>Here's What's happening with your store today.</p> */}</div>
                <CustomDatePicker startDate={dates?.start} endDate={dates?.end} onChange={onDateChange} />
            </div>

            {pageLoad ? (
                <SkeletonDashboard />
            ) : (
                <div className="layout-dashboard additional-dashboard">
                    <div className="grid col-12">
                        <div className="md:col-3">
                            <div className={`card widget-overview-box widget-overview-box`}>
                                <div className="overview-top">
                                    <span className="overview-title">Total Users</span>
                                </div>
                                <div className="flex justify-content-between earning-overview">
                                    <div className="overview-detail flex justify-content-between align-items-center w-full gap-2 flex-wrap">
                                        <div className="overview-badge flex align-items-center pl-0">
                                            <p className="total-earning">{!window.cn(dashboardData) ? dashboardData.TotalUsers : 0}</p>
                                        </div>

                                        <img width="40" height="40" src="/assets/images/total-user.png" alt="" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="md:col-3">
                            <div className={`card widget-overview-box widget-overview-box`}>
                                <div className="overview-top">
                                    <span className="overview-title">Fresh Registration</span>
                                </div>
                                <div className="flex justify-content-between earning-overview">
                                    <div className="overview-detail flex justify-content-between align-items-center w-full gap-2 flex-wrap">
                                        <div className="overview-badge flex  align-items-center pl-0">
                                            <p className="total-earning">{!window.cn(dashboardData) ? dashboardData.TotalFreshApp : 0}</p>
                                        </div>
                                        <img width="40" height="40" src="/assets/images/fresh_registration.png" alt="" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="md:col-3">
                            <div className={`card widget-overview-box widget-overview-box`}>
                                <div className="overview-top">
                                    <span className="overview-title">Renew Application</span>
                                </div>
                                <div className="flex justify-content-between earning-overview">
                                    <div className="overview-detail flex justify-content-between align-items-center w-full gap-2 flex-wrap">
                                        <div className="overview-badge flex  align-items-center pl-0">
                                            <p className="total-earning">{!window.cn(dashboardData) ? dashboardData.TotalRenewApp : 0}</p>
                                        </div>
                                        <img width="40" height="40" src="/assets/images/renewal.png" alt="" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="md:col-3">
                            <div className={`card widget-overview-box widget-overview-box`}>
                                <div className="overview-top">
                                    <span className="overview-title">Re-Entry Application</span>
                                </div>
                                <div className="flex justify-content-between earning-overview">
                                    <div className="overview-detail flex justify-content-between align-items-center w-full gap-2 flex-wrap">
                                        <div className="overview-badge flex  align-items-center pl-0">
                                            <p className="total-earning">{!window.cn(dashboardData) ? dashboardData.TotalReentryApp : 0}</p>
                                        </div>
                                        <img width="40" height="40" src="/assets/images/reentry.png" alt="" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid col-12">
                        <div className="col-8 ">
                            <div className="card">
                                <div className="card-header mb-2">
                                    <span className="overview-text-h font-bold">Application</span>
                                    <Calendar value={applicationYear} onChange={(e) => handleDateChange(e.value)} view="year" dateFormat="yy" showIcon />
                                </div>
                                <div className="graph">
                                    <Chart type="line" id="revenue-chart" data={totalOrderData} options={totalOrderOptions}></Chart>
                                </div>
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="card widget-timeline">
                                <div className="timeline-header flex justify-content-between align-items-center">
                                    <p className="title">Transaction history</p>
                                </div>
                                <div className="timeline-content">
                                    {!window.cn(dashboardData?.payment_history) && dashboardData.payment_history.length > 0 ? (
                                        <Timeline value={!window.cn(dashboardData) ? dashboardData.payment_history : ''} marker={marker} content={content} className="custimized-timeline" />
                                    ) : (
                                        <>
                                            <div className="flex flex-column justify-content-center align-items-center my-4">
                                                <img width="50" height="50" src="/assets/images/no-data.png" alt="" />
                                                <p>No Transactions Found</p>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="timeline-footer flex align-items-center justify-content-center"></div>
                            </div>
                        </div>
                    </div>

                    <div className="grid col-12">
                        <div className="col-4">
                            <div className="card widget-country-graph">
                                <div className="country-title title">Pending Applications</div>
                                {dashboardData?.pending_apps_list && !Object.values(dashboardData?.pending_apps_list).every((value) => value === 0) ? (
                                    <>
                                        <div className="country-graph flex justify-content-center">
                                            <Chart type="doughnut" id="country-chart" data={countryChart} options={countryChartOptions} style={{ position: 'relative', width: '75%' }}></Chart>
                                        </div>
                                        <div className="country-content">
                                            <ul>
                                                <li className="flex justify-content-between align-items-center">
                                                    <div className="flex justify-content-between align-items-center">
                                                        <div
                                                            className="color"
                                                            style={{
                                                                backgroundColor: '#0F8BFD',
                                                                boxShadow: '0px 0px 10px rgba(0, 208, 222, 0.3)'
                                                            }}
                                                        ></div>
                                                        <span>Degree Addition</span>
                                                    </div>
                                                    <span>{!window.cn(dashboardData?.pending_apps_list) ? dashboardData?.pending_apps_list?.degree_addtion : 0}</span>
                                                </li>
                                                <li className="flex justify-content-between align-items-center">
                                                    <div className="flex justify-content-between align-items-center">
                                                        <div
                                                            className="color"
                                                            style={{
                                                                backgroundColor: '#545C6B',
                                                                boxShadow: '0px 0px 10px rgba(0, 208, 222, 0.3)'
                                                            }}
                                                        ></div>
                                                        <span>Duplicate Certy</span>
                                                    </div>
                                                    <span>{!window.cn(dashboardData?.pending_apps_list) ? dashboardData?.pending_apps_list?.duplicate_certy : 0}</span>
                                                </li>
                                                <li className="flex justify-content-between align-items-center">
                                                    <div className="flex justify-content-between align-items-center">
                                                        <div
                                                            className="color"
                                                            style={{
                                                                backgroundColor: '#EC4DBC',
                                                                boxShadow: '0px 0px 10px rgba(135, 62, 254, 0.3)'
                                                            }}
                                                        ></div>
                                                        <span>Good Standing</span>
                                                    </div>
                                                    <span>{!window.cn(dashboardData?.pending_apps_list) ? dashboardData?.pending_apps_list?.good_standing : 0}</span>
                                                </li>
                                                <li className="flex justify-content-between align-items-center">
                                                    <div className="flex justify-content-between align-items-center">
                                                        <div
                                                            className="color"
                                                            style={{
                                                                backgroundColor: '#EEE500',
                                                                boxShadow: '0px 0px 10px rgba(252, 97, 97, 0.3)'
                                                            }}
                                                        ></div>
                                                        <span>Name Change</span>
                                                    </div>
                                                    <span>{!window.cn(dashboardData?.pending_apps_list) ? dashboardData?.pending_apps_list?.name_change : 0}</span>
                                                </li>
                                                <li className="flex justify-content-between align-items-center">
                                                    <div className="flex justify-content-between align-items-center">
                                                        <div
                                                            className="color"
                                                            style={{
                                                                backgroundColor: '#FC6161',
                                                                boxShadow: '0px 0px 10px rgba(238, 229, 0, 0.3)'
                                                            }}
                                                        ></div>
                                                        <span>Re-Entry</span>
                                                    </div>
                                                    <span>{!window.cn(dashboardData?.pending_apps_list) ? dashboardData?.pending_apps_list?.reentry : 0}</span>
                                                </li>
                                                <li className="flex justify-content-between align-items-center">
                                                    <div className="flex justify-content-between align-items-center">
                                                        <div
                                                            className="color"
                                                            style={{
                                                                backgroundColor: '#00D0DE',
                                                                boxShadow: '0px 0px 10px rgba(236, 77, 188, 0.3)'
                                                            }}
                                                        ></div>
                                                        <span>Renew</span>
                                                    </div>
                                                    <span>{!window.cn(dashboardData?.pending_apps_list) ? dashboardData?.pending_apps_list?.renew : 0} </span>
                                                </li>
                                            </ul>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex flex-column justify-content-center align-items-center pb-6 pt-3">
                                            <img width="50" height="50" src="/assets/images/no-data.png" alt="" />
                                            <p>No Pending Application Found</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="col-8 data-table-activites ">
                            <div className="section-left">{!window.cn(dashboardData?.upcoming_refreshers) && dashboardData?.upcoming_refreshers.length > 0 && <CustomDataTable refresher={dashboardData?.upcoming_refreshers} />}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* 
            <section
        id="hero-animated"
        className="hero-animated d-flex align-items-center"
      >
        <div
          className="container d-flex flex-column justify-content-center align-items-center text-center position-relative"
          data-aos="zoom-out"
        >
          <img
            src="/assets/images/gspc-inner-logo-new.png"
            className="img-fluid animated"
            alt="blk-logo"
            width={250}
          />
          <p className="mb-0">Hello Admin</p>
          <h2>
            Welcome to <span>Gujarat State Pharmacy Council</span>
          </h2>
        </div>
      </section> */}
        </>
    );
};

export default Dashboard;
